import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import i18n from "@/i18n";

const AuthContext = createContext(null);

// When a user is signed in, switch the UI to their preferred language. This
// runs whenever `user.preferred_language` changes (on login or refresh). For
// anonymous visitors the browser-language-detector decides.
function syncLanguage(user) {
    const code = user?.preferred_language;
    if (code && i18n.resolvedLanguage !== code) {
        i18n.changeLanguage(code);
    }
}

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null); // null = checking, false = anon, object = authed
    const [loading, setLoading] = useState(true);
    const lastRefreshAt = useRef(0);

    // Pulls the latest /auth/me into state — used to pick up server-side
    // changes (e.g. admin flipping a tool on for this user, credits granted)
    // without forcing the user to log out and back in. Throttled to once
    // every 20s so route changes don't hammer the API.
    const refreshUser = useCallback(async ({ force = false } = {}) => {
        const token = localStorage.getItem("vsf_token");
        if (!token) return;
        const now = Date.now();
        if (!force && now - lastRefreshAt.current < 20_000) return;
        lastRefreshAt.current = now;
        try {
            const r = await api.get("/auth/me");
            setUser((prev) => {
                // Only update if something actually changed to avoid re-renders
                if (!prev || JSON.stringify(prev) !== JSON.stringify(r.data)) return r.data;
                return prev;
            });
        } catch (e) {
            // Silent — initial mount already handles 401 sign-out
        }
    }, []);

    useEffect(() => {
        const token = localStorage.getItem("vsf_token");
        if (!token) {
            setUser(false);
            setLoading(false);
            return;
        }
        api.get("/auth/me")
            .then((r) => { setUser(r.data); syncLanguage(r.data); })
            .catch(() => {
                localStorage.removeItem("vsf_token");
                setUser(false);
            })
            .finally(() => {
                setLoading(false);
                lastRefreshAt.current = Date.now();
            });
    }, []);

    // Refresh on tab focus + every 60s while tab is visible — picks up any
    // admin-side change (tool activation, credit grant) without the user
    // having to log out / back in.
    useEffect(() => {
        if (!user) return undefined;
        const interval = setInterval(() => refreshUser(), 60_000);
        const onVisibility = () => {
            if (document.visibilityState === "visible") refreshUser();
        };
        document.addEventListener("visibilitychange", onVisibility);
        return () => {
            clearInterval(interval);
            document.removeEventListener("visibilitychange", onVisibility);
        };
    }, [user, refreshUser]);

    const login = async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            localStorage.setItem("vsf_token", data.token);
            setUser(data.user);
            syncLanguage(data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const register = async (payload) => {
        try {
            const { data } = await api.post("/auth/register", payload);
            if (data.pending) {
                return { ok: true, pending: true, message: data.message };
            }
            localStorage.setItem("vsf_token", data.token);
            setUser(data.user);
            return { ok: true, user: data.user };
        } catch (e) {
            return { ok: false, error: formatApiErrorDetail(e.response?.data?.detail) || e.message };
        }
    };

    const logout = () => {
        localStorage.removeItem("vsf_token");
        setUser(false);
    };

    return (
        <AuthContext.Provider value={{ user, loading, login, register, logout, setUser, refreshUser }}>
            {children}
        </AuthContext.Provider>
    );
}

export const useAuth = () => useContext(AuthContext);
